// Exchange — inline detail drawer matching the prototype's
// `.detail-row`. Renders three sections (Agent consensus, Signal
// timeline, Resolution & tradability) wired to real Supabase hooks:
//   - useSignalCalibrationSummary (platform-level calibration)
//   - useSignalTimeline (per-signal lifecycle events)
//   - signal-provenance helper (data source descriptions)
//
// No hardcoded agent dialogue, votes, kickoffs, or fixture state.

import { Icon } from "../../components/icons/Icon.tsx";
import { useSignalCalibrationSummary } from "../../exchange/useSignalCalibrationSummary.ts";
import { useSignalTimeline } from "../../vault/useSignalTimeline.ts";
import { getProvenanceItems } from "../../exchange/signal-provenance.ts";
import type { PublicSignalCardViewModel } from "../../exchange/usePublicSignalCards.ts";
import { formatCanonical } from "../../exchange/market-format.ts";

interface Props {
  card: PublicSignalCardViewModel;
  isWatched: boolean;
  onToggleWatch: (id: string) => void;
}

function formatRelative(iso: string, now: Date): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = now.getTime() - t;
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "ago" : "ahead";
  const m = Math.floor(abs / 60000);
  if (m < 60) return `${String(m)}m ${sign}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}h ${sign}`;
  const d = Math.floor(h / 24);
  return `${String(d)}d ${sign}`;
}

function formatExpires(iso: string | null, now: Date): string {
  if (iso === null) return "Window open";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = t - now.getTime();
  if (diff <= 0) return "Closed";
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `in ${String(h)}h`;
  const d = Math.floor(h / 24);
  return `in ${String(d)}d ${String(h - d * 24)}h`;
}

export function ExchangeDetailDrawer({ card, isWatched, onToggleWatch }: Props): JSX.Element {
  const calibration = useSignalCalibrationSummary();
  const timeline = useSignalTimeline(card.id);
  const provenance = getProvenanceItems();
  const now = new Date();

  const edge = card.value_edge;
  const edgeText =
    edge === null ? "—" : `${edge > 0 ? "+" : ""}${edge.toFixed(1)}%`;
  const fairPct = card.fair_probability === null
    ? "—"
    : `${(card.fair_probability > 1 ? card.fair_probability : card.fair_probability * 100).toFixed(1)}%`;
  const impliedPct = card.market_implied_probability === null
    ? "—"
    : `${(card.market_implied_probability > 1
        ? card.market_implied_probability
        : card.market_implied_probability * 100).toFixed(1)}%`;

  // Derive agent votes truthfully from the card's own metrics. Each
  // "agent" mirrors a deterministic dimension of the curated card —
  // we never invent a model debate.
  const edgeVote = edge !== null && edge > 0 ? "Take" : "Caution";
  const confVote = card.confidence_label === "high" ? "Take" : "Caution";
  const riskVote = card.risk_label === "low" ? "Take" : "Caution";
  const tierVote = card.data_tier === 1 || card.data_tier === 2 ? "Take" : "Caution";

  const consensusTakes = [edgeVote, confVote, riskVote, tierVote].filter((v) => v === "Take").length;
  const consensus = `${String(consensusTakes)} of 4`;

  return (
    <div className="detail-row">
      <div className="detail-inner">
        <div className="detail-section">
          <h4>Agent consensus · {consensus}</h4>
          <div className="agent-list">
            <div className={`agent ${edgeVote === "Caution" ? "dissent" : ""}`}>
              <div className="badge">A</div>
              <div>
                <div className="role">Edge vs market</div>
                <div className="reason">
                  Fair {fairPct} vs market implied {impliedPct} → edge {edgeText}.
                </div>
              </div>
              <div className="vote">{edgeVote}</div>
            </div>
            <div className={`agent ${confVote === "Caution" ? "dissent" : ""}`}>
              <div className="badge">B</div>
              <div>
                <div className="role">Agent confidence</div>
                <div className="reason">
                  Confidence label · {card.confidence_label ?? "unknown"}.
                  {calibration.summary !== null
                    ? ` Platform calibration status: ${calibration.summary.status}.`
                    : ""}
                </div>
              </div>
              <div className="vote">{confVote}</div>
            </div>
            <div className={`agent ${riskVote === "Caution" ? "dissent" : ""}`}>
              <div className="badge">C</div>
              <div>
                <div className="role">Tradability risk</div>
                <div className="reason">
                  Risk label · {card.risk_label ?? "unknown"}. Probability view only — manual
                  placement on your platform of choice.
                </div>
              </div>
              <div className="vote">{riskVote}</div>
            </div>
            <div className={`agent ${tierVote === "Caution" ? "dissent" : ""}`}>
              <div className="badge">D</div>
              <div>
                <div className="role">Data tier</div>
                <div className="reason">
                  {card.data_tier === null
                    ? "Tier unknown."
                    : `Tier ${String(card.data_tier)} data sources backing this card.`}
                </div>
              </div>
              <div className="vote">{tierVote}</div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h4>Signal timeline</h4>
          <div className="timeline">
            <div className="tl-row done">
              <div className="marker" />
              <div className="lbl">Detected by Scout</div>
              <div className="when">{formatRelative(card.generated_at, now)}</div>
            </div>
            {timeline.loading ? (
              <div className="tl-row">
                <div className="marker" />
                <div className="lbl">Loading lifecycle events…</div>
                <div className="when">—</div>
              </div>
            ) : null}
            {timeline.events.map((ev) => (
              <div className="tl-row done" key={ev.id}>
                <div className="marker" />
                <div className="lbl">{ev.event_type}</div>
                <div className="when">{formatRelative(ev.created_at, now)}</div>
              </div>
            ))}
            <div className="tl-row now">
              <div className="marker" />
              <div className="lbl">Observation window open</div>
              <div className="when">now</div>
            </div>
            <div className="tl-row">
              <div className="marker" />
              <div className="lbl">Signal closes</div>
              <div className="when">{formatExpires(card.expires_at, now)}</div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h4>Resolution &amp; tradability</h4>
          <div className="kv-list">
            <div className="kv">
              <span className="k">Selection</span>
              <span className="v">{card.signal_label ?? "—"}</span>
            </div>
            <div className="kv">
              <span className="k">Market</span>
              <span className="v">{formatCanonical(card.canonical_code)}</span>
            </div>
            <div className="kv">
              <span className="k">Fair odds</span>
              <span className="v">
                {card.fair_odds_decimal === null ? "—" : card.fair_odds_decimal.toFixed(2)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Market odds</span>
              <span className="v">
                {card.market_odds_decimal === null ? "—" : card.market_odds_decimal.toFixed(2)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Risk</span>
              <span className="v" style={{ textTransform: "capitalize" }}>
                {card.risk_label ?? "unknown"}
              </span>
            </div>
            <div className="kv">
              <span className="k">Data tier</span>
              <span className="v">
                {card.data_tier === null ? "—" : `Tier ${String(card.data_tier)}`}
              </span>
            </div>
            <div className="kv">
              <span className="k">Data source</span>
              <span className="v">{provenance[0]?.label ?? "Curated pipeline"}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="detail-foot">
        <div className="note">
          Phase 1A deterministic estimate. Not a bookmaker offer. Manual placement only — Strikza
          never places, logs in, or holds funds.
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={() => onToggleWatch(card.id)}>
            {isWatched ? "Remove from watchlist" : "Add to watchlist"}
          </button>
          <button className="btn" type="button">
            View reasoning trail
          </button>
          <button className="btn primary" type="button">
            Open signal <Icon name="arrow" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
